export type Unit = 'kg' | 'lb';

/**
 * 表示用の単位ラベル。保存値は 'lb' のままにして (既存データとの互換)、
 * 画面では一般的な 'lbs' 表記で出す。
 */
export const unitLabel = (unit: Unit): string => (unit === 'lb' ? 'lbs' : 'kg');

export const UNIT_OPTIONS: { value: Unit; label: string }[] = [
  { value: 'kg', label: 'kg' },
  { value: 'lb', label: 'lbs' },
];

/**
 * 種目ごとの測り方。
 *  weight … 重さ × 回数 × セット
 *  reps   … 回数 × セット (自重)
 *  hold   … 秒 × セット   (プランクなど)
 *  cardio … 時間(分) + 距離(km)
 */
export type EntryKind = 'weight' | 'reps' | 'hold' | 'cardio';

export type Entry = {
  exercise: string;
  kind: EntryKind;
  weight: number;
  unit: Unit;
  reps: number;
  sets: number;
  seconds: number;
  minutes: number;
  distanceKm: number;
};

export type Workout = {
  date: string;
  entries: Entry[];
  /** その日の体重 (kg)。未計測の日は undefined。 */
  bodyWeight?: number;
  /** その日の体脂肪率 (%)。未計測の日は undefined。 */
  bodyFat?: number;
  note?: string;
  updatedAt: string;
};

/** 体組成だけ記録した日 (トレーニング休み) も「記録あり」として扱う */
export const hasContent = (w: Pick<Workout, 'entries' | 'bodyWeight' | 'bodyFat'>): boolean =>
  w.entries.length > 0 || w.bodyWeight !== undefined || w.bodyFat !== undefined;

/** 編集中の行。入力途中の空文字を許すため数値は文字列で保持する。 */
export type DraftEntry = {
  id: string;
  exercise: string;
  kind: EntryKind;
  weight: string;
  unit: Unit;
  reps: string;
  sets: string;
  seconds: string;
  minutes: string;
  distanceKm: string;
  /** 器具ごとの前回値を引き継いだときの、その記録の日付 */
  carriedFrom?: string;
};

export const newDraft = (seed?: Partial<DraftEntry>): DraftEntry => ({
  id: crypto.randomUUID(),
  exercise: '',
  kind: 'weight',
  weight: '',
  unit: 'kg',
  reps: '',
  sets: '1',
  seconds: '',
  minutes: '',
  distanceKm: '',
  ...seed,
});

export const toDraft = (e: Entry, carriedFrom?: string): DraftEntry => ({
  id: crypto.randomUUID(),
  exercise: e.exercise,
  kind: e.kind,
  weight: e.weight ? String(e.weight) : '',
  unit: e.unit,
  reps: e.reps ? String(e.reps) : '',
  sets: String(e.sets || 1),
  seconds: e.seconds ? String(e.seconds) : '',
  minutes: e.minutes ? String(e.minutes) : '',
  distanceKm: e.distanceKm ? String(e.distanceKm) : '',
  carriedFrom,
});

const num = (v: string) => Number(v) || 0;

/** 種目名が空の行は捨てる。数値の空欄は 0 (セット数のみ 1) とみなす。 */
export const toEntries = (drafts: DraftEntry[]): Entry[] =>
  drafts
    .filter((d) => d.exercise.trim() !== '')
    .map((d) => ({
      exercise: d.exercise.trim(),
      kind: d.kind,
      weight: num(d.weight),
      unit: d.unit,
      reps: Math.round(num(d.reps)),
      sets: Math.max(1, Math.round(num(d.sets) || 1)),
      seconds: Math.round(num(d.seconds)),
      minutes: num(d.minutes),
      distanceKm: num(d.distanceKm),
    }));

const kg = (weight: number, unit: Unit) => (unit === 'lb' ? weight * 0.45359237 : weight);

/** 重量種目のみボリュームを持つ */
export const volumeOfEntry = (e: Entry) =>
  e.kind === 'weight' ? kg(e.weight, e.unit) * e.reps * e.sets : 0;

export const volumeOf = (entries: Entry[]): number =>
  entries.reduce((sum, e) => sum + volumeOfEntry(e), 0);

/** その日の合計。カレンダーの濃淡と褒め言葉の強度で使う。 */
export function totalsOf(entries: Entry[]) {
  let volumeKg = 0;
  let sets = 0;
  let cardioMinutes = 0;
  let holdSeconds = 0;
  let distanceKm = 0;

  for (const e of entries) {
    volumeKg += volumeOfEntry(e);
    if (e.kind !== 'cardio') sets += e.sets;
    cardioMinutes += e.minutes;
    holdSeconds += e.seconds * e.sets;
    distanceKm += e.distanceKm;
  }

  return {
    volumeKg,
    sets,
    cardioMinutes,
    holdSeconds,
    distanceKm,
    exercises: new Set(entries.map((e) => e.exercise)).size,
  };
}
